// frontend/src/farm/farmRules.mjs
// Pure farm rules, mirrored from backend/app/game/farm.py so the client can act optimistically
// (tile flips, coins float) and reconcile with the server's snapshot afterwards. No Phaser,
// no config.js (node --test).
import { cellKey, inFarm } from './iso.mjs';
import { treeStage } from './farmObjects.mjs';   // a cycle, fine: only functions cross it
import { applyAnimalOp, releaseInto, sendBack } from './farmAnimals.mjs';
import { sellBack } from '../state/resale.mjs';

export const HAND_DURATION_MS = 600;
export const ACTION_LABEL = { plow: 'Zaoraj', plant: 'Posadź', harvest: 'Zbierz', pick_seed: 'Wybierz nasiona' };
export const ACTION_ICON = { plow: '{hoe}', plant: '{seedling}', harvest: '{basket}', remove_plot: '{shovel}' };   // remove_plot = Zasyp
const MAX_CELLS_PER_OP = 16;

// The stage the player should SEE now: a planted plot whose ready_at passed on the client
// clock already looks ripe instead of waiting for the next server poll.
export function plotStage(plot, nowMs) {
  if (!plot || !plot.state) return 'grass';
  if (plot.state === 'planted') {
    if (plot.ready_at_iso && Date.parse(plot.ready_at_iso) <= nowMs) return 'ready';
    return 'growing';
  }
  return plot.state;   // fallow | plowed | ready
}

// Which of the three drawn stages a crop is in: seedling for the first half of its timer, then
// growing, then ready. Pure, because the hall's garden window needs it as much as the farm does.
export function cropStageKey(plot, stage, nowMs) {
  if (stage === 'ready') return 'ready';
  const planted = plot.planted_at_iso ? Date.parse(plot.planted_at_iso) : nowMs;
  const total = (plot.grow_seconds ?? 1) * 1000;
  return (nowMs - planted) / total < 0.5 ? 'seedling' : 'growing';
}

export function stageAt(farm, cell, nowMs) {
  return plotStage(farm.cells[cellKey(cell.x, cell.y)], nowMs);
}

export function inferAction(stage, { seed = null } = {}) {
  if (stage === 'grass' || stage === 'fallow') return 'plow';
  if (stage === 'plowed') return seed ? 'plant' : 'pick_seed';
  if (stage === 'ready') return 'harvest';
  return null;
}

// Zasyp (owner 2026-09-29): the cells of a tap / dragged line that can go back to grass — empty
// fields only (plowed, or harvested = fallow); a crop and plain grass are left alone.
export function recyclableCells(farm, cells, nowMs) {
  return cells.filter((c) => ['plowed', 'fallow'].includes(stageAt(farm, c, nowMs)));
}

export function eligibleCells(farm, cells, action, nowMs) {
  return cells.filter((c) => inferAction(stageAt(farm, c, nowMs), { seed: 'any' }) === action);
}

// Round half up on both sides (Python's round() is banker's; the server uses int(v + 0.5)).
// Mirror of farm_catalog.LEVEL_CURVE (the parity test checks every level against the server).
export const LEVEL_CURVE = 1.28;
export function xpForLevel(level) { return Math.floor(10 * Math.pow(LEVEL_CURVE, level - 1) + 0.5); }

export function levelProgress(xp) {
  let level = 1;
  let rest = xp;
  while (level < 100 && rest >= xpForLevel(level)) { rest -= xpForLevel(level); level++; }
  return { level, xpIntoLevel: rest, xpForLevel: xpForLevel(level) };
}

export function nextExpansion(size, farmCatalog) {
  const ladder = [farmCatalog.start_size, ...farmCatalog.expansions.map((e) => e.size)];
  if (!ladder.includes(size)) return null;
  return farmCatalog.expansions.find((e) => e.size > size) ?? null;
}

// Mirror of farm.py regen_fuel: tank units per regen_seconds, capped, stored as a float.
function refilled(fuel, cfg, nowMs) {
  const secs = Math.max(0, (nowMs - Date.parse(fuel.updated_at_iso)) / 1000);
  return Math.min(cfg.tank, fuel.units + secs * cfg.tank / cfg.regen_seconds);
}
export function fuelNow(farm, cfg, nowMs) { return refilled(farm.fuel, cfg, nowMs); }
export function regenFuel(farm, cfg, nowMs) {
  farm.fuel = { units: refilled(farm.fuel, cfg, nowMs), updated_at_iso: new Date(nowMs).toISOString() };
}

// "2h", "2h 30min", "1d", "2d 12h": a zero part is left out, and past a day minutes drop off.
// Mirror of farm_catalog.mastered_sell_price: +sell_bonus per mastery star (rounded half up),
// but at least +1 coin per star.
export function masteredSellPrice(crop, stars, farmCatalog) {
  const bonus = farmCatalog?.mastery?.sell_bonus ?? 0;
  const base = crop?.sell_price ?? 0, n = stars ?? 0;
  if (!bonus) return base;
  return Math.max(Math.floor(base * (1 + bonus * n) + 0.5), base + n);
}

export function humanizeRemaining(seconds) {
  if (seconds <= 0) return 'Gotowe!';
  const two = (big, bigUnit, small, smallUnit) => `${big}${bigUnit}${small ? ` ${small}${smallUnit}` : ''}`;
  if (seconds >= 86400) return two(Math.floor(seconds / 86400), 'd', Math.floor((seconds % 86400) / 3600), 'h');
  if (seconds >= 3600) return two(Math.floor(seconds / 3600), 'h', Math.floor((seconds % 3600) / 60), 'min');
  if (seconds >= 60) return `${Math.floor(seconds / 60)}min`;
  return `${Math.ceil(seconds)}s`;
}

export function tooltipFor(plot, stage, crops, nowMs) {
  const name = plot?.seed ? (crops[plot.seed]?.name_pl ?? plot.seed) : '';
  if (stage === 'grass') return 'Trawa — kliknij, by zaorać';
  if (stage === 'fallow') return 'Ugór — kliknij, by zaorać';
  if (stage === 'plowed') return 'Zaorane — wybierz nasiona i posadź';
  if (stage === 'ready') return `${name} — gotowe! {sparkle}`;
  // `stage` is still growing, so there IS time left — round the last fraction of a second
  // UP, or humanizeRemaining(0) turns the countdown into "jeszcze Gotowe! ⏳".
  const left = plot?.ready_at_iso ? Math.max(1, Math.ceil((Date.parse(plot.ready_at_iso) - nowMs) / 1000)) : 1;
  return `${name} — jeszcze ${humanizeRemaining(left)} {hourglass}`;
}

// ---- optimistic apply (mirror of farm.py) --------------------------------------------

const reject = (reason) => ({ ok: false, reason });

function normCells(farm, cells) {
  if (!cells || cells.length === 0) return { error: 'FARM_NO_CELLS' };
  const seen = new Map();
  for (const c of cells) {
    const x = c.x ?? c[0], y = c.y ?? c[1];
    if (!inFarm({ x, y }, farm.size)) return { error: 'FARM_CELL_OUT_OF_BOUNDS' };
    seen.set(cellKey(x, y), { x, y });
  }
  if (seen.size > MAX_CELLS_PER_OP) return { error: 'FARM_TOO_MANY_CELLS' };
  return { cells: [...seen.values()] };
}

// Mirror of farm.py add_xp: every level gained pays a coin gift (catalog.farm.level_gift_per_level).
// Mirror of farm.py double_xp: the welcome-back hour, XP counts double until rested_until_iso.
export function doubleXp(farm, nowMs) {
  const until = farm?.rested_until_iso;
  return !!until && nowMs < Date.parse(until);
}

function addXp(state, n, nowMs) {
  let add = Number.isFinite(n) ? n : 0;
  const before = state.level ?? 1;
  if (add > 0 && doubleXp(state.farm, nowMs)) add *= 2;
  state.xp = (state.xp ?? 0) + add;
  state.level = levelProgress(state.xp).level;
  const per = state.catalog?.farm?.level_gift_per_level ?? 0;
  for (let lvl = before + 1; lvl <= state.level; lvl++) state.money += per * lvl;
}

// Mirror of farm.py _count_harvest: one harvest toward mastery; each star crossed pays once.
function countHarvest(state, seed, nowMs) {
  const fc = state.catalog.farm;
  const spec = fc.crops[seed];
  const farm = state.farm;
  farm.mastery = farm.mastery ?? {};
  const m = farm.mastery[seed] ?? (farm.mastery[seed] = { harvested: 0, stars: 0 });
  m.harvested += 1;
  const thresholds = fc.mastery?.thresholds?.[spec?.band];
  if (!thresholds) return;
  while (m.stars < 3 && m.harvested >= thresholds[m.stars]) {
    const { xp, sell_mult: mult } = fc.mastery.rewards[m.stars];
    m.stars += 1;
    state.money += mult * spec.sell_price;
    if (m.stars === 3) { farm.storage ??= {}; farm.storage[`plaque:${seed}`] = (farm.storage[`plaque:${seed}`] ?? 0) + 1; }
    addXp(state, xp, nowMs);
  }
}

// ---- objects on the farm (mirror of farm_objects.py) ---------------------------------
let localIds = 0;
const objSpec = (fc, item) => (item?.startsWith('plaque:') ? fc.objects?.plaque : fc.objects?.[item]);
const farmPlacements = (state) => (state.placements ??= []).filter((p) => p.scene === 'farm');
function footprintCellsOf(fc, item, x, y) {
  const s = objSpec(fc, item); const out = [];
  if (!s) return out;                                   // an unknown item (old data): takes no cells
  for (let dx = 0; dx < s.w; dx++) for (let dy = 0; dy < s.h; dy++) out.push([x + dx, y + dy]);
  return out;
}
// What a hand tap on `cell` would do (the phone's action button, 'Zaoraj' / 'Zbierz' …): nothing
// off the farm or where a building / decoration stands (it used to offer 'Zaoraj' on the Kurnik).
export function handActionAt(state, cell, nowMs, { seed = null } = {}) {
  if (!state?.farm || !inFarm(cell, state.farm.size)) return null;
  if (occupiedCells(state).has(cellKey(cell.x, cell.y))) return null;
  return inferAction(stageAt(state.farm, cell, nowMs), { seed });
}

export function occupiedCells(state, excludeId = null) {
  const fc = state.catalog.farm; const out = new Set();
  for (const p of farmPlacements(state)) if (p.id !== excludeId) for (const [x, y] of footprintCellsOf(fc, p.item, p.x, p.y)) out.add(cellKey(x, y));
  return out;
}
function fits(state, item, x, y, excludeId = null) {
  const farm = state.farm; const taken = occupiedCells(state, excludeId);
  for (const [cx, cy] of footprintCellsOf(state.catalog.farm, item, x, y)) {
    if (!inFarm({ x: cx, y: cy }, farm.size)) return 'FARM_CELL_OUT_OF_BOUNDS';
    if (farm.cells[cellKey(cx, cy)] || taken.has(cellKey(cx, cy))) return 'FARM_CELL_TAKEN';
  }
  return null;
}
const bucketOf = (state, item) => (objSpec(state.catalog.farm, item).kind === 'decoration'
  ? ((state.inventory ??= {}).unplaced_decorations ??= {})
  : (state.farm.storage ??= {}));

function applyObjectOp(state, op, nowMs) {
  const fc = state.catalog.farm; const farm = state.farm;
  const find = () => farmPlacements(state).find((p) => p.id === op.id);
  switch (op.op) {
    case 'buy_object': {
      const spec = fc.objects?.[op.item];
      if (!spec) return reject('FARM_UNKNOWN_OBJECT');
      if (spec.kind === 'plaque') return reject('FARM_NOT_FOR_SALE');
      const qty = Math.max(1, op.qty ?? 1);
      if ((state.level ?? 1) < spec.level) return reject('FARM_LEVEL_TOO_LOW');
      if (spec.unique && (qty !== 1 || (farm.storage?.[op.item] ?? 0) > 0 || farmPlacements(state).some((p) => p.item === op.item))) return reject('FARM_LANDMARK_OWNED');
      if (state.money < spec.price * qty) return reject('INSUFFICIENT_MONEY_FARM');
      state.money -= spec.price * qty;
      const b = bucketOf(state, op.item); b[op.item] = (b[op.item] ?? 0) + qty;
      farm.landmarks_bought ??= [];
      const first = !spec.unique || !farm.landmarks_bought.includes(op.item);
      if (spec.unique && first) farm.landmarks_bought.push(op.item);
      if (first) addXp(state, Math.floor(spec.price / 100) * qty, nowMs);   // 1 %: cheap objects give none
      return { ok: true };
    }
    case 'place': {
      if (!objSpec(fc, op.item)) return reject('FARM_UNKNOWN_OBJECT');
      const b = bucketOf(state, op.item);
      if ((b[op.item] ?? 0) < 1) return reject('FARM_NOT_IN_STORAGE');
      const bad = fits(state, op.item, op.x, op.y); if (bad) return reject(bad);
      b[op.item] -= 1;
      const spec = objSpec(fc, op.item);
      const placed = { id: `local-${++localIds}`, scene: 'farm', type: spec.kind, item: op.item, x: op.x, y: op.y, flip: !!op.flip };
      if (spec.kind === 'tree') {                   // always a fresh sapling (the server's place)
        Object.assign(placed, { planted_at_iso: new Date(nowMs).toISOString(), ripe_at_iso: new Date(nowMs + spec.sapling_s * 1000).toISOString(), grown: false });
      }
      state.placements.push(placed);
      if (spec.kind === 'hut') releaseInto(state, placed, nowMs);          // 4c: waiting animals move in
      return { ok: true };
    }
    case 'place_fence': {
      const n = normCells(farm, op.cells); if (n.error) return reject(n.error);
      if (new Set(n.cells.map((c) => c.x)).size > 1 && new Set(n.cells.map((c) => c.y)).size > 1) return reject('FARM_FENCE_NOT_STRAIGHT');
      for (const c of n.cells) { const bad = fits(state, 'plot', c.x, c.y); if (bad) return reject(bad); }
      const price = fc.objects.plot.price;
      if (state.money < price * n.cells.length) return reject('INSUFFICIENT_MONEY_FARM');
      state.money -= price * n.cells.length;
      for (const c of n.cells) state.placements.push({ id: `local-${++localIds}`, scene: 'farm', type: 'fence', item: 'plot', x: c.x, y: c.y, flip: false });
      addXp(state, Math.floor(price / 100) * n.cells.length, nowMs);
      return { ok: true };
    }
    case 'move': { const p = find(); if (!p) return reject('FARM_OBJECT_NOT_FOUND');
      const bad = fits(state, p.item, op.x, op.y, p.id); if (bad) return reject(bad); p.x = op.x; p.y = op.y; return { ok: true }; }
    case 'flip': { const p = find(); if (!p) return reject('FARM_OBJECT_NOT_FOUND'); p.flip = !p.flip; return { ok: true }; }
    case 'pick_up': { const p = find(); if (!p) return reject('FARM_OBJECT_NOT_FOUND');
      if (p.type === 'hut') sendBack(state, p, nowMs);                        // 4c: its animals wait again
      state.placements.splice(state.placements.indexOf(p), 1); const b = bucketOf(state, p.item); b[p.item] = (b[p.item] ?? 0) + 1; return { ok: true }; }
    case 'harvest_tree': { const p = find(); if (!p) return reject('FARM_OBJECT_NOT_FOUND');
      const spec = objSpec(fc, p.item);
      if (spec.kind !== 'tree' || treeStage(p, nowMs) !== 'ripe') return reject('FARM_TREE_NOT_READY');
      state.money += spec.payout;
      if (spec.harvest_xp) addXp(state, spec.harvest_xp, nowMs);
      p.grown = true; p.ripe_at_iso = new Date(nowMs + spec.cycle_s * 1000).toISOString();
      return { ok: true, coins: spec.payout, xp: spec.harvest_xp ?? 0 }; }
    default: { const p = find(); if (!p) return reject('FARM_OBJECT_NOT_FOUND');          // sell_object
      const spec = objSpec(fc, p.item); if (spec.kind === 'plaque') return reject('FARM_NOT_FOR_SALE');
      if (p.type === 'hut') sendBack(state, p, nowMs);
      state.placements.splice(state.placements.indexOf(p), 1);
      const old = (state.farm.old_price_objects ??= {});            // bought before the rebalance: 80 % of the OLD price
      const sale = sellBack(old, fc.legacy_object_prices, p.item, spec.price);
      if (sale.old) old[p.item] -= 1;
      state.money += sale.coins;
      return { ok: true }; }
  }
}

const NEEDS = {
  plow: (s) => s === 'grass' || s === 'fallow',
  plant: (s) => s === 'plowed',
  harvest: (s) => s === 'ready',
};

// Mirror of farm.py _vehicle_cells + the vehicle branches of plow/plant/harvest (same check order).
function applyVehicleOp(state, op, spec, nowMs) {
  const fc = state.catalog.farm;
  const farm = state.farm;
  const tool = fc.tools?.[op.tool];
  if (!tool) return reject('FARM_UNKNOWN_TOOL');
  if (tool.action !== op.op) return reject('FARM_TOOL_ACTION');
  const size = farm.tools?.[op.tool] ?? 0;
  if (!size) return reject('FARM_TOOL_NOT_OWNED');
  const n = normCells(farm, op.cells);
  if (n.error) return reject(n.error);
  const xs = n.cells.map((c) => c.x), ys = n.cells.map((c) => c.y);
  if (Math.max(...xs) - Math.min(...xs) >= size || Math.max(...ys) - Math.min(...ys) >= size) return reject('FARM_FOOTPRINT');
  regenFuel(farm, fc.fuel, nowMs);
  const taken = op.op === 'plow' ? occupiedCells(state) : new Set();          // objects: skipped
  let work = n.cells.filter((c) => !taken.has(cellKey(c.x, c.y)) && NEEDS[op.op](stageAt(farm, c, nowMs)));
  if (!work.length) return reject('FARM_NO_CELLS');
  if (op.op !== 'harvest') {
    const price = op.op === 'plow' ? fc.plow_cost : spec.seed_price;
    work = work.slice(0, Math.floor(state.money / price));
    if (!work.length) return reject('INSUFFICIENT_MONEY_FARM');
  }
  if (farm.fuel.units < work.length) return reject('FARM_NO_FUEL');
  farm.fuel.units -= work.length;
  // Every cell in `work` passes the hand path's checks by construction.
  const r = applyOp(state, { op: op.op, cells: work.map((c) => [c.x, c.y]), seed: op.seed, color: op.color }, nowMs);
  return r.ok ? { ok: true, worked: work.length } : r;
}

// The farm grows to the right-bottom and right-top, the gate corner and everything by it stay put
// (owner, 2026-09-29). Cells are numbered from the top corner, so every farm y shifts by the growth
// — the server's _grow_right, exactly.
function growRight(state, farm, d) {
  farm.cells = Object.fromEntries(Object.entries(farm.cells ?? {}).map(([k, v]) => {
    const [x, y] = k.split(',').map(Number);
    return [`${x},${y + d}`, v];
  }));
  for (const p of state.placements ?? []) if (p.scene === 'farm') p.y = Number(p.y) + d;
  for (const a of state.animals ?? []) if (a.scene === 'farm' && a.y != null) a.y += d;
}

// A plot's sprite family: the plain crop, or `<crop>_<colour>` for a non-start flower colour (Kwiaty §7).
export const cropArtId = (seed, color) => (color ? `${seed}_${color}` : seed);

export function applyOp(state, op, nowMs) {
  const fc = state.catalog.farm;
  const farm = state.farm;
  const kind = op.op;
  if (kind === 'expand') {
    const next = nextExpansion(farm.size, fc);
    if (!next) return reject('FARM_MAX_SIZE');
    if ((state.level ?? 1) < next.min_level) return reject('FARM_LEVEL_TOO_LOW');
    if (state.money < next.cost) return reject('INSUFFICIENT_MONEY_FARM');
    state.money -= next.cost;
    growRight(state, farm, next.size - farm.size);
    farm.size = next.size;
    addXp(state, Math.max(1, Math.floor(next.cost / 100)), nowMs);
    return { ok: true };
  }
  if (kind === 'buy_tool') {
    const spec = fc.tools?.[op.tool];
    if (!spec) return reject('FARM_UNKNOWN_TOOL');
    const owned = farm.tools?.[op.tool] ?? 0;
    const tier = spec.tiers.find((t) => t.size > owned);
    if (!tier) return reject('FARM_TOOL_MAX');
    if ((state.level ?? 1) < tier.level) return reject('FARM_LEVEL_TOO_LOW');
    if (state.money < tier.price) return reject('INSUFFICIENT_MONEY_FARM');
    state.money -= tier.price;
    farm.tools = { ...farm.tools, [op.tool]: tier.size };
    addXp(state, Math.max(1, Math.floor(tier.price / 100)), nowMs);
    return { ok: true };
  }
  if (kind === 'buy_konewka') {                         // owner 2026-10-27: once, any level (farm.buy_konewka)
    const price = fc.konewka?.price;
    if (price == null) return reject('FARM_UNKNOWN_OP');
    if (farm.konewka) return reject('FARM_KONEWKA_OWNED');
    if (state.money < price) return reject('INSUFFICIENT_MONEY_FARM');
    state.money -= price;
    farm.konewka = true;
    addXp(state, Math.max(1, Math.floor(price / 100)), nowMs);
    return { ok: true };
  }
  if (kind === 'refuel') {
    regenFuel(farm, fc.fuel, nowMs);
    if (farm.fuel.units >= fc.fuel.tank) return reject('FARM_TANK_FULL');
    if (state.money < fc.fuel.refill_cost) return reject('INSUFFICIENT_MONEY_FARM');
    state.money -= fc.fuel.refill_cost;
    farm.fuel.units = fc.fuel.tank;
    return { ok: true };
  }
  if (kind === 'use_can') {
    if ((farm.fuel_cans ?? 0) < 1) return reject('FARM_NO_CANS');
    regenFuel(farm, fc.fuel, nowMs);
    farm.fuel.units = Math.min(fc.fuel.tank, farm.fuel.units + fc.fuel.can_units);
    farm.fuel_cans -= 1;
    return { ok: true };
  }
  if (kind === 'seen_gifts') {             // forget only the senders the pop-up showed
    farm.new_gifts = op.count == null ? [] : (farm.new_gifts ?? []).slice(Math.max(0, op.count));
    return { ok: true };
  }
  if (['buy_animal', 'collect_animal', 'feed_animal', 'pet_animal'].includes(kind)) {
    const r = applyAnimalOp(state, op, nowMs);
    if (r.ok && kind === 'collect_animal') {                  // mirror of farm_animals.collect: egg +1, milk +2 XP
      const type = (state.animals ?? []).find((a) => a.id === op.id)?.type;
      const per = fc.animals?.[type]?.product_xp ?? 0;
      if (per) addXp(state, per * r.count, nowMs);
    }
    return r;
  }
  if (kind === 'seen_notice') { farm.animal_notice = false; return { ok: true }; }
  if (kind === 'seen_helpers') {           // farm 5: forget only the helpers the pop-up showed (like seen_gifts)
    const shown = op.ids == null ? null : new Set(op.ids);
    farm.helpers = shown ? Object.fromEntries(Object.entries(farm.helpers ?? {}).filter(([k]) => !shown.has(k))) : {};
    return { ok: true };
  }
  if (['buy_object', 'place', 'place_fence', 'move', 'flip', 'pick_up', 'sell_object', 'harvest_tree'].includes(kind)) return applyObjectOp(state, op, nowMs);
  if (!['plow', 'plant', 'harvest', 'remove_plot'].includes(kind)) return reject('FARM_UNKNOWN_OP');

  // The server checks the crop before the cells for `plant` (farm.py::plant checks
  // FarmUnknownCrop / FarmCropLocked before _cells), so a doubly-invalid op (bad cells AND
  // an unknown/locked seed) must report the same reason on both sides.
  let spec;
  if (kind === 'plant') {
    spec = fc.crops[op.seed];
    if (!spec) return reject('FARM_UNKNOWN_CROP');
    if ((state.level ?? 1) < spec.level) return reject('FARM_CROP_LOCKED');
    const colors = spec.colors ?? [];
    if (op.color != null) {                     // a flower colour (Kwiaty §7): its stars, before the cells
      if (!colors.includes(op.color)) return reject('FARM_COLOR_UNKNOWN');
      if ((farm.mastery?.[op.seed]?.stars ?? 0) < colors.indexOf(op.color)) return reject('FARM_COLOR_LOCKED');
    }
  }
  if (op.tool && kind !== 'remove_plot') return applyVehicleOp(state, op, spec, nowMs);

  const n = normCells(farm, op.cells);
  if (n.error) return reject(n.error);
  const cells = n.cells;
  const stages = cells.map((c) => stageAt(farm, c, nowMs));

  if (kind === 'plow') {
    if (stages.some((s) => s !== 'grass' && s !== 'fallow')) return reject('FARM_CELL_NOT_PLOWABLE');
    const taken = occupiedCells(state);                       // never plow under an object
    if (cells.some((c) => taken.has(cellKey(c.x, c.y)))) return reject('FARM_CELL_TAKEN');
    const cost = fc.plow_cost * cells.length;
    if (state.money < cost) return reject('INSUFFICIENT_MONEY_FARM');
    state.money -= cost;
    for (const c of cells) farm.cells[cellKey(c.x, c.y)] = { state: 'plowed', fertilized: false };
    addXp(state, fc.plow_xp * cells.length, nowMs);
    return { ok: true };
  }
  if (kind === 'plant') {
    if (stages.some((s) => s !== 'plowed')) return reject('FARM_CELL_NOT_PLOWED');
    const cost = spec.seed_price * cells.length;
    if (state.money < cost) return reject('INSUFFICIENT_MONEY_FARM');
    state.money -= cost;
    const plantedAt = new Date(nowMs);
    const readyAt = new Date(nowMs + spec.grow_seconds * 1000);
    for (const c of cells) {
      farm.cells[cellKey(c.x, c.y)] = {
        state: 'planted', seed: op.seed, fertilized: false, pv: fc.plot_price_version ?? 2,
        planted_at_iso: plantedAt.toISOString(), grow_seconds: spec.grow_seconds,
        ready_at_iso: readyAt.toISOString(), seconds_remaining: spec.grow_seconds,
        ...(op.color != null && op.color !== (spec.colors ?? [])[0] ? { c: op.color } : {}),
      };
    }
    addXp(state, spec.xp * cells.length, nowMs);
    return { ok: true };
  }
  if (kind === 'harvest') {
    if (stages.some((s) => s !== 'ready')) return reject('FARM_CELL_NOT_READY');
    let xp = 0;
    for (const c of cells) {
      const key = cellKey(c.x, c.y);
      const plot = farm.cells[key], seed = plot.seed;
      const crop = fc.crops[seed];
      // Mirror of farm.py harvest: a plot planted before the 2026-10-20 rebalance (no pv) sells at its old price.
      const priced = plot.pv != null ? crop : { ...crop, sell_price: fc.legacy_sell?.[seed] ?? crop.sell_price };
      state.money += masteredSellPrice(priced, farm.mastery?.[seed]?.stars ?? 0, fc);
      xp += crop.harvest_xp ?? 0;
      countHarvest(state, seed, nowMs);
      farm.cells[key] = { state: 'fallow', fertilized: false };
    }
    if (xp) addXp(state, xp, nowMs);                          // once, after the loop, like the server
    return { ok: true };
  }
  // remove_plot — "Zasyp": an empty field (plowed or harvested) back to grass; a crop is protected
  if (stages.some((s) => s === 'grass')) return reject('FARM_CELL_NOT_FALLOW');
  if (stages.some((s) => s !== 'fallow' && s !== 'plowed')) return reject('FARM_CELL_HAS_CROP');
  for (const c of cells) delete farm.cells[cellKey(c.x, c.y)];
  return { ok: true };
}
